import {Box, Button, Card, CardProps, Chip, IconButton, Typography} from "@mui/material";
import React, {ReactNode, useMemo} from "react";
import {_DataCardsBottomActions, _DataCardsTopActions, DataCardsDataType, DataCardsProperty} from "./index";
import UIUtils from "../../../utils";


export interface DataCardProps<T extends DataCardsDataType> {
    data: T,
    topActions?: _DataCardsTopActions<T>,
    bottomActions?: _DataCardsBottomActions<T>,
    title?: ReactNode | ((data: T) => ReactNode),
    properties?: DataCardsProperty<T>[],
    child?: ReactNode | ((data: T) => ReactNode),
    cardProps?: CardProps;
}

const DataCard = <T extends DataCardsDataType, >(props: DataCardProps<T>) => {
    const {title: _title, child: _children, topActions: _topActions, bottomActions: _bottomActions} = props;
    const title = useMemo(() => typeof _title === 'function' ? _title(props.data) : _title, [props.data, _title]);
    const children = useMemo(() => typeof _children === 'function' ? _children(props.data) : _children, [props.data, _children]);
    const topActions = useMemo(() => typeof _topActions === 'function' ? _topActions(props.data) : _topActions, [props.data, _topActions]);
    const bottomActions = useMemo(() => typeof _bottomActions === 'function' ? _bottomActions(props.data) : _bottomActions, [props.data, _bottomActions]);

    return (
        <>
            <Card
                variant={'outlined'}
                {...props.cardProps ?? {}}
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    gap: 1,
                    ...UIUtils.invokeSx(props.cardProps?.sx, theme) as any,
                })}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "space-between"}}>
                    <Typography variant={'body1'} fontWeight={600}>
                        {title ?? '--'}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "flex-end", gap: 0.5}}>
                        {
                            topActions?.map((action) => (
                                <IconButton
                                    {...action}
                                    key={action.key}
                                />
                            ))
                        }
                    </Box>
                </Box>
                {
                    props.properties
                        ?.filter(property => !property.hidden)
                        ?.map((property) => {
                            const obtainedTitle = typeof property.title === 'function' ? property.title(props.data[property.propertyName], props.data) : property.title;
                            const obtainedValue = typeof property.value === 'function' ? property.value(props.data[property.propertyName], props.data) : property.value;
                            const obtainedValueProps = typeof property.valueProps === 'function' ? property.valueProps(props.data[property.propertyName], props.data) : property.valueProps;
                            return (
                                <Box key={property.key} sx={{display: 'flex', alignItems: 'center', justifyContent: "space-between"}}>
                                    <Typography variant={'caption'} fontWeight={400}>
                                        {obtainedTitle ?? "--"}
                                    </Typography>
                                    <Chip
                                        {...obtainedValueProps ?? {}}
                                        variant={'outlined'}
                                        size={'small'}
                                        label={obtainedValue ?? "--"}
                                    />
                                </Box>
                            );
                        })
                }
                {
                    children
                }
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "flex-end", gap: 1}}>
                    {
                        bottomActions?.map((action) => (
                            <Button
                                {...action}
                                key={action.key}
                            />
                        ))
                    }
                </Box>
            </Card>
        </>
    );
}

export default DataCard;
